import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

type RightRailTab = 'pbp' | 'home' | 'away';

export interface ViewingToolsHeaderProps {
  activeTab: RightRailTab;
  onTabChange: (tab: RightRailTab) => void;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamId: number;
  awayTeamId: number;
  focusTeamId?: number;
  onCreateCustomEvent: () => void;
  isCelebration?: boolean;
}

const TABS: { value: RightRailTab }[] = [
  { value: 'pbp' },
  { value: 'away' },
  { value: 'home' },
];

const ViewingToolsHeader = React.memo(function ViewingToolsHeader({
  activeTab,
  onTabChange,
  homeTeamName,
  awayTeamName,
  homeTeamId,
  awayTeamId,
  focusTeamId,
  onCreateCustomEvent,
  isCelebration = false,
}: ViewingToolsHeaderProps) {
  const { viewOpponentsShifts } = useFlags();

  const tabLabels: Record<RightRailTab, string> = {
    pbp: 'Play-By-Play',
    home: homeTeamName,
    away: awayTeamName,
  };

  const visibleTabs = TABS.filter(({ value }) => {
    if (value === 'pbp') return true;
    if (viewOpponentsShifts) return true;
    if (focusTeamId === homeTeamId) return value === 'home';
    if (focusTeamId === awayTeamId) return value === 'away';
    return true;
  });

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Tab bar — hidden for celebration games (no play-by-play / team shifts to switch between) */}
        {isCelebration ? (
          <Box />
        ) : (
          <Stack direction="row" spacing={2}>
            {visibleTabs.map(({ value }) => (
              <Typography
                key={value}
                onClick={() => onTabChange(value)}
                sx={{
                  fontFamily: 'Sohne',
                  fontSize: '20px',
                  lineHeight: '32px',
                  fontWeight: activeTab === value ? 500 : 400,
                  color: activeTab === value ? 'white' : 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    color: activeTab === value ? 'white' : 'rgba(255,255,255,0.65)',
                  },
                }}
              >
                {tabLabels[value]}
              </Typography>
            ))}
          </Stack>
        )}

        {/* New Custom Event button — visible on PBP tab, or always for celebration games (no tabs) */}
        {(isCelebration || activeTab === 'pbp') && (
          <Button
            onClick={onCreateCustomEvent}
            sx={{ padding: '7.5px 16px 7.5px 20px', textTransform: 'none' }}
            variant="contained"
            color="primary"
            size="small"
            endIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M8.33301 7.66699H13V8.33301H8.33301V13H7.66699V8.33301H3V7.66699H7.66699V3H8.33301V7.66699Z"
                  fill="white"
                />
              </svg>
            }
          >
            New Custom Event
          </Button>
        )}
      </Stack>
    </Box>
  );
});
ViewingToolsHeader.displayName = 'ViewingToolsHeader';

export default ViewingToolsHeader;
